"use client";

import Image from "next/image";

export const dynamic = "force-dynamic";
import { signIn } from "next-auth/react";
import { useRef, useState } from "react";
import Link from "next/link";

export default function Page({ searchParams }: { searchParams?: { callbackUrl: string; alreadyUser: string } }) {
  const textRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link href="/">
            <Image className="mx-auto h-20 w-auto" src="/images/ikura_logo.png" alt="ikura" height={256} width={256} />
          </Link>
          <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            {searchParams?.alreadyUser
              ? `Sign in to your account`
              : `Sign up to access your passport to authentic Japan travel`}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form
              className="space-y-6"
              onSubmit={(e) => {
                const email = textRef.current?.value;
                if (email) {
                  setLoading(true);
                  e.preventDefault();

                  signIn("email", { email, callbackUrl: searchParams?.callbackUrl ?? "/" });
                } else {
                  e.preventDefault();
                }
              }}
            >
              <div>
                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    ref={textRef}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-cta px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-cta-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  disabled={loading}
                >
                  {searchParams?.alreadyUser ? `Sign in` : `Sign up`}
                </button>
              </div>
            </form>

            <div>
              <div className="relative mt-10">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-white px-6 text-gray-900">Or continue with</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4">
                <button
                  className="gsi-material-button"
                  onClick={() => {
                    signIn("google", { callbackUrl: searchParams?.callbackUrl ?? "/" });
                  }}
                >
                  <div className="gsi-material-button-state"></div>
                  <div className="gsi-material-button-content-wrapper">
                    <div className="gsi-material-button-icon">
                      <svg
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 48 48"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        style={{ display: "block" }}
                      >
                        <path
                          fill="#EA4335"
                          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                        ></path>
                        <path
                          fill="#4285F4"
                          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                        ></path>
                        <path
                          fill="#FBBC05"
                          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                        ></path>
                        <path
                          fill="#34A853"
                          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                        ></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                      </svg>
                    </div>
                    <span className="gsi-material-button-contents">
                      {searchParams?.alreadyUser ? `Sign in` : `Sign up`} with Google
                    </span>
                    <span style={{ display: "none" }}>
                      {searchParams?.alreadyUser ? `Sign in` : `Sign up`} with Google
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {!searchParams?.alreadyUser && (
              <div className="pt-5">
                <span>
                  By clicking ‘Sign up’ or ‘Sign up with Google’ button, you agree to the ikura{" "}
                  <a href="https://www.ikura.io/terms" className="font-semibold underline" target="_blank">
                    Terms
                  </a>{" "}
                  and{" "}
                  <a href="https://www.ikura.io/privacy" className="font-semibold underline" target="_blank">
                    Privacy Policy
                  </a>
                  .
                </span>
              </div>
            )}

            <div className="pt-5">
              {searchParams?.alreadyUser ? (
                <>
                  Don&apos;t have an account?{" "}
                  <Link
                    href={`/auth/signin?${searchParams?.callbackUrl ? `callbackUrl=${searchParams?.callbackUrl}` : ""}`}
                  >
                    Sign up
                  </Link>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <Link
                    href={`/auth/signin?alreadyUser=1${
                      searchParams?.callbackUrl ? `&callbackUrl=${searchParams?.callbackUrl}` : ""
                    }`}
                    className="font-semibold underline"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
